import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Mail, Send, Loader2, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const STATUS_META = {
  sent: { color: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle, label: 'Sent' },
  failed: { color: 'bg-red-100 text-red-800', Icon: XCircle, label: 'Failed' },
  skipped_no_activity: { color: 'bg-slate-100 text-slate-600', Icon: Clock, label: 'Skipped (no activity)' },
};

/**
 * Admin panel for the Weekly Feedback Digest — one-click trigger + audit trail.
 */
export default function DigestAdminPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/digest/logs', { params: { limit: 100 } });
      setLogs(res.data.logs || []);
    } catch { setLogs([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const sendNow = async () => {
    if (!window.confirm('Send the weekly digest email to every parent now?')) return;
    setSending(true);
    try {
      const res = await api.post('/admin/digest/send-now', { force: true });
      toast.success(`Digest run complete — sent ${res.data.sent} · skipped ${res.data.skipped}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Digest send failed');
    } finally { setSending(false); }
  };

  const stats = logs.reduce((acc, l) => {
    acc[l.status || 'unknown'] = (acc[l.status || 'unknown'] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="digest-admin-root">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900" data-testid="digest-title">Weekly Feedback Digest</h1>
        <p className="text-slate-500 font-semibold">Automated Sunday 9 AM parent email summarising every class feedback of the past week (delivered in each parent&apos;s local timezone).</p>
      </div>

      <div className="glass-card rounded-xl p-6 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Mail className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#0F172A]">Send now</h3>
            <p className="text-xs text-slate-500">Fires the digest immediately to every parent (idempotent per week).</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={load} variant="outline" size="sm" data-testid="digest-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          <Button onClick={sendNow} disabled={sending} className="bg-indigo-600 hover:bg-indigo-700" data-testid="digest-send-now">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send digest now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total runs" value={logs.length} color="bg-slate-100 text-slate-700" />
        <StatCard label="Sent" value={stats.sent || 0} color="bg-emerald-100 text-emerald-800" />
        <StatCard label="Skipped (empty week)" value={stats.skipped_no_activity || 0} color="bg-slate-100 text-slate-600" />
        <StatCard label="Failed" value={stats.failed || 0} color="bg-red-100 text-red-800" />
      </div>

      <div className="glass-card rounded-xl overflow-hidden" data-testid="digest-logs">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
          <p className="text-sm font-bold text-slate-900">Recent digest runs</p>
          <span className="text-xs text-slate-500">{logs.length} entries</span>
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500">No digest runs yet — click &ldquo;Send digest now&rdquo; to trigger the first one.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Parent</th>
                  <th className="text-left px-4 py-2">Week</th>
                  <th className="text-center px-4 py-2">Children</th>
                  <th className="text-center px-4 py-2">Feedback</th>
                  <th className="text-center px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2">Sent at</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => {
                  const meta = STATUS_META[l.status] || { color: 'bg-slate-100 text-slate-600', Icon: Clock, label: l.status || '—' };
                  const Icon = meta.Icon;
                  return (
                    <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`digest-log-${l.id}`}>
                      <td className="px-4 py-2 font-mono text-xs">{l.parent_email || '—'}</td>
                      <td className="px-4 py-2 text-xs text-slate-600">{l.week_key || '—'}</td>
                      <td className="px-4 py-2 text-center">{l.children_count ?? 0}</td>
                      <td className="px-4 py-2 text-center">{l.feedback_count ?? 0}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${meta.color}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-xs text-slate-500">{(l.sent_at || '').slice(0, 16).replace('T', ' ')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className={`rounded-xl p-4 ${color}`}>
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</p>
    </div>
  );
}
